import {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  MAX_MENTIONS_PER_REQUEST,
  MentionCandidate,
  MentionQuery,
} from '../types';
import { placeCaretAfterChip } from './caret';
import { createMentionChipElement } from './dom-chip';
import {
  flattenMentionSections,
  isMentionSectionTitleRow,
  MentionDropdownItem,
  MentionPopupSection,
} from './MentionPopup';
import {
  ActiveTrigger,
  findActiveTrigger,
  trimMentionQuery,
} from './trigger-detection';

interface MentionDropdownApi {
  handleEnter: () => void;
  focusedItemIndex: number;
  setFocusedItemIndex: (index: number) => void;
  setCurrentAction: (action: 'hover' | 'focus') => void;
  getFocusedIndexForArrowKey: (key: 'ArrowUp' | 'ArrowDown') => number;
}

export interface UseMentionPopupArgs {
  editorRef: RefObject<HTMLDivElement | null>;
  emitChange: () => void;
  /** Results for the query last reported via `onQueryChange`. */
  sections: MentionPopupSection[];
  loading: boolean;
  /** Reports the trigger + search text a consumer should fetch results for. */
  onQueryChange?: (query: MentionQuery | null) => void;
}

export interface UseMentionPopupResult {
  activeTrigger: ActiveTrigger | null;
  setActiveTrigger: (trigger: ActiveTrigger | null) => void;
  applyTrigger: (trigger: ActiveTrigger) => void;
  refreshTriggerFromEditor: (root: HTMLElement) => void;
  dropdownItems: MentionDropdownItem[];
  isSearching: boolean;
  hadMatchForActiveTrigger: boolean;
  dropdownApiRef: RefObject<MentionDropdownApi>;
  insertCandidate: (candidate: MentionCandidate) => void;
  handlePopupKeyDown: (e: KeyboardEvent<HTMLDivElement>) => boolean;
}

export const useMentionPopup = ({
  editorRef,
  emitChange,
  sections: sourceSections,
  loading,
  onQueryChange,
}: UseMentionPopupArgs): UseMentionPopupResult => {
  const dropdownApiRef = useRef<MentionDropdownApi>({
    handleEnter: () => {},
    focusedItemIndex: -1,
    setFocusedItemIndex: () => {},
    setCurrentAction: () => {},
    getFocusedIndexForArrowKey: () => -1,
  });
  // Sticky match for the active trigger — see hadMatchForActiveTrigger below.
  const matchRef = useRef<{
    trigger: '@' | '#';
    query: string;
    matched: boolean;
  } | null>(null);
  const [activeTrigger, setActiveTrigger] = useState<ActiveTrigger | null>(
    null
  );

  // Tracks the last query actually reported via `onQueryChange` — `applyTrigger`
  // fires on every editor refresh (clicks, caret moves), not just query edits,
  // so we only re-report when the trigger or trimmed query text really changed.
  const lastReportedQueryRef = useRef<MentionQuery | null>(null);

  const closeTrigger = useCallback(() => {
    setActiveTrigger(null);
    lastReportedQueryRef.current = null;
    onQueryChange?.(null);
  }, [onQueryChange]);

  const applyTrigger = useCallback(
    (trigger: ActiveTrigger) => {
      setActiveTrigger(prev => {
        if (
          prev &&
          prev.trigger === trigger.trigger &&
          prev.query === trigger.query &&
          prev.startOffset === trigger.startOffset &&
          prev.startNode === trigger.startNode &&
          prev.unresolvedElement === trigger.unresolvedElement
        ) {
          return prev;
        }
        return trigger;
      });

      const query = trimMentionQuery(trigger.query);
      const last = lastReportedQueryRef.current;
      if (last && last.trigger === trigger.trigger && last.query === query) {
        return;
      }
      lastReportedQueryRef.current = { trigger: trigger.trigger, query };
      onQueryChange?.({ trigger: trigger.trigger, query });
    },
    [onQueryChange]
  );

  const refreshTriggerFromEditor = useCallback(
    (root: HTMLElement) => {
      const trigger = findActiveTrigger(root);
      if (trigger) {
        applyTrigger(trigger);
        return;
      }
      closeTrigger();
    },
    [applyTrigger, closeTrigger]
  );

  // Hide whatever the consumer last reported once the popup itself is closed.
  const sections = activeTrigger ? sourceSections : [];

  const dropdownItems: MentionDropdownItem[] = useMemo(
    () => flattenMentionSections(sections),
    [sections]
  );

  // Dropdown only paints the focus highlight while its internal
  // `currentAction` is 'focus' — a prior mouse hover flips it to 'hover'
  // and nothing resets it, so the auto-focused first item silently stops
  // rendering as focused after any hover. Force it back on every refresh
  // (new trigger or new results) so the highlight is never invisible.
  useEffect(() => {
    dropdownApiRef.current.setCurrentAction('focus');
  }, [activeTrigger, dropdownItems]);

  const isSearching = !!activeTrigger && loading;

  if (!activeTrigger) {
    matchRef.current = null;
  } else {
    const { trigger, query } = activeTrigger;
    const prev = matchRef.current;
    if (!prev || prev.trigger !== trigger || prev.query !== query) {
      const inherit =
        !!prev &&
        prev.matched &&
        prev.trigger === trigger &&
        prev.query.length > 0 &&
        query.startsWith(prev.query);
      matchRef.current = { trigger, query, matched: inherit };
    }
    if (!isSearching) {
      const hasCandidates =
        trimMentionQuery(query).length > 0 &&
        dropdownItems.some(item => !isMentionSectionTitleRow(item));
      matchRef.current = { trigger, query, matched: hasCandidates };
    }
  }

  const hadMatchForActiveTrigger = matchRef.current?.matched ?? false;

  const insertCandidate = useCallback(
    (candidate: MentionCandidate) => {
      const trigger = activeTrigger;
      const root = editorRef.current;
      if (!trigger || !root) {
        return;
      }

      if (
        root.querySelectorAll('[data-mention-chip]').length >=
        MAX_MENTIONS_PER_REQUEST
      ) {
        closeTrigger();
        return;
      }

      const chip = createMentionChipElement(candidate);
      const space = document.createTextNode('\u00a0');

      if (trigger.unresolvedElement?.isConnected) {
        trigger.unresolvedElement.replaceWith(chip, space);
        placeCaretAfterChip(chip);
        closeTrigger();
        root.focus();
        emitChange();
        return;
      }

      if (trigger.startNode == null || trigger.startOffset == null) {
        closeTrigger();
        return;
      }

      const range = document.createRange();
      const nodeLength = trigger.startNode.nodeValue?.length ?? 0;
      const endOffset = Math.min(
        trigger.startOffset + 1 + trigger.query.length,
        nodeLength
      );
      range.setStart(trigger.startNode, trigger.startOffset);
      range.setEnd(trigger.startNode, endOffset);
      range.deleteContents();

      range.insertNode(space);
      range.insertNode(chip);

      root.normalize();
      placeCaretAfterChip(chip);

      closeTrigger();
      root.focus();
      emitChange();
    },
    [activeTrigger, closeTrigger, editorRef, emitChange]
  );

  const skipTitleRows = useCallback(
    (startIndex: number, direction: 'ArrowUp' | 'ArrowDown'): number => {
      let index = startIndex;
      for (
        let guard = 0;
        guard < dropdownItems.length &&
        dropdownItems[index] &&
        isMentionSectionTitleRow(dropdownItems[index]);
        guard++
      ) {
        index =
          direction === 'ArrowDown'
            ? (index + 1) % dropdownItems.length
            : (index - 1 + dropdownItems.length) % dropdownItems.length;
      }
      return index;
    },
    [dropdownItems]
  );

  const selectFocusedMention = useCallback((): boolean => {
    const api = dropdownApiRef.current;
    let index = api.focusedItemIndex;
    if (
      dropdownItems.length > 0 &&
      (index < 0 || isMentionSectionTitleRow(dropdownItems[index]))
    ) {
      index = skipTitleRows(Math.max(index, 0), 'ArrowDown');
    }

    const item = dropdownItems[index];
    if (item && !isMentionSectionTitleRow(item)) {
      insertCandidate(item);
      return true;
    }

    closeTrigger();
    return true;
  }, [closeTrigger, dropdownItems, insertCandidate, skipTitleRows]);

  const handlePopupKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): boolean => {
      const api = dropdownApiRef.current;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        api.setCurrentAction('focus');
        api.setFocusedItemIndex(
          skipTitleRows(api.getFocusedIndexForArrowKey(e.key), e.key)
        );
        return true;
      }
      if (e.key === 'Escape') {
        closeTrigger();
        return true;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (e.nativeEvent.isComposing) {
          return false;
        }
        return selectFocusedMention();
      }
      return false;
    },
    [closeTrigger, selectFocusedMention, skipTitleRows]
  );

  return {
    activeTrigger,
    setActiveTrigger: closeTriggerAwareSetter(closeTrigger, setActiveTrigger),
    applyTrigger,
    refreshTriggerFromEditor,
    dropdownItems,
    isSearching,
    hadMatchForActiveTrigger,
    dropdownApiRef,
    insertCandidate,
    handlePopupKeyDown,
  };
};

// `setActiveTrigger(null)` is the external API `MentionComposerInput` uses to
// close the popup (outside click, blur) — route it through `closeTrigger` so
// `onQueryChange(null)` fires in lockstep, without changing that call site.
const closeTriggerAwareSetter =
  (
    closeTrigger: () => void,
    setActiveTrigger: (trigger: ActiveTrigger | null) => void
  ) =>
  (trigger: ActiveTrigger | null) => {
    if (trigger === null) {
      closeTrigger();
      return;
    }
    setActiveTrigger(trigger);
  };
